// /**
//  * Warehouses - list
//  */

// React
import React, {useContext} from 'react';
import {Link} from 'react-router-dom';
import HeaderBuilder from '../../../utils/headerBuilder';
// UI
import theme from '../../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiPencilOutline,
  mdiChevronDown,
  mdiFileDocumentOutline,
  mdiChevronUp,
  mdiAccountPlus,
  mdiHistory,
} from '@mdi/js';

// Helper
import {
  formaterPhoneNumber,
  translateParcelsStatus,
} from '../../../utils/helpers';

//Component
import jMoment from 'moment-jalaali';

// Type
import {Parcels} from '../interfaces';
import {
  rulesAdminChangeParcelStatus,
  rulesParcelStatus,
} from '../../../utils/rulesParcelStatus';
import {rulesAdminChangeOrderStatus} from '../../../utils/rulesOrderStatus';
import AuthContext from '../../../authContext';

type Data = Parcels;
type Props = {
  data: any;
  handleOpenConfirmDisable: (id: number) => void;
  handleOpenConfirmActive: (id: number) => void;
  handleOpen: (id: number, type: string) => void;
  changeStatus: any;
};

type Ord = 'asc' | 'desc';

interface HeadCell {
  // id: keyof Data;
  id: any;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      whiteSpace: 'nowrap',
    },
    body: {
      fontSize: 14,
      whiteSpace: 'nowrap',
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    columnText: {
      padding: '0 10px',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const headCells: HeadCell[] = [
  {
    id: 'sourceWarehouseName',
    label: 'مبدا',
  },
  {
    id: 'id',
    label: 'شناسه محموله',
  },
  {
    id: 'shippingDate',
    label: 'تاریخ ارسال',
  },
  {
    id: 'deliveryDate',
    label: 'تاریخ تحویل',
  },
  {
    id: 'itemsCount',
    label: 'تعداد تنوع',
  },
  {
    id: 'inventoryItemsCount',
    label: 'تعداد آیتم',
  },
  {
    id: 'status',
    label: 'وضعیت',
  },
];

const IconSort = (order: Ord) => {
  return (
    <Icon path={order === 'asc' ? mdiChevronDown : mdiChevronUp} size={1} />
  );
};

function EnhancedTableHead(props: EnhancedTableProps) {
  const {order, orderBy, onRequestSort} = props;
  const classes = useStyles();
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => {
          const orderStatus = orderBy === headCell.id ? order : 'asc';
          return (
            <StyledTableCell
              key={headCell.id}
              align="center"
              sortDirection={orderBy === headCell.id ? order : false}
              data-cy={headCell.id}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderStatus}
                onClick={createSortHandler(headCell.id)}
                IconComponent={() => IconSort(orderStatus)}
              >
                <span className={classes.columnText}>{headCell.label}</span>
              </TableSortLabel>
            </StyledTableCell>
          );
        })}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  // const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const [orderBy, setOrderBy] = React.useState<any>('id');
  const [dense] = React.useState(false);
  const authContext = useContext(AuthContext);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <TableContainer component={Paper}>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <HeaderBuilder
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
            filterVal={props.filterVal}
            onFilterValue={(filtervalues: any) => {
              props.onFilterValue(filtervalues);
            }}
            head={props.head}
            removeFilterHandler={props.removeFilterHandler}
            haveAction={true}
            filterLabelTranslator={props.filterLabelTranslator}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                let color = '';
                let backgroundColor = '';

                switch (true) {
                  case row.status === 'UNLOADING' ||
                    row.status === 'UNLOADED' ||
                    row.status === 'REGISTERED':
                    backgroundColor = '#d1e8fa';
                    color = '#283c74';
                    break;
                  case row.status === 'CANCELLED' || row.status === 'RETURNING':
                    backgroundColor = '#fdd1cc';
                    color = '#611a15';
                    break;
                  case row.status === 'LOCATED' || row.status === 'DELIVERED':
                    backgroundColor = '#d7fcd7';
                    color = '#1e4659';
                    break;
                  case row.status === 'CONFLICTED' || row.status === 'ASSIGNED':
                    backgroundColor = '#ffebcf';
                    color = '#663c40';
                    break;
                  case row.status === 'SHIPPED' || row.status === 'SUBMITTED':
                    backgroundColor = 'white';
                    color = 'black';
                }

                return (
                  <TableRow
                    style={{backgroundColor: backgroundColor}}
                    key={index}
                  >
                    <TableCell style={{color: color}} align="center">
                      {row.sourceWarehouseName}
                      {row.sellerName && `(${row.sellerName})`}
                    </TableCell>
                    <TableCell
                      style={{color: color}}
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.id}
                    </TableCell>
                    <TableCell style={{color: color}} align="center">
                      <span>
                        {jMoment(row.shippingDate, 'YYYY/M/D')
                          .locale('fa')
                          .format('jYY/jMM/jDD')}
                      </span>
                    </TableCell>{' '}
                    <TableCell style={{color: color}} align="center">
                      <span>
                        {jMoment(row.deliveryDate, 'YYYY/M/D')
                          .locale('fa')
                          .format('jYY/jMM/jDD')}
                      </span>
                    </TableCell>
                    <TableCell style={{color: color}} align="center">
                      {row.itemsCount}
                    </TableCell>
                    <TableCell style={{color: color}} align="center">
                      {row.inventoryItemsCount}
                    </TableCell>
                    <TableCell style={{color: color}} align="center">
                      <FormControl style={{width: '100%'}}>
                        <Icon
                          path={mdiPencilOutline}
                          size={1}
                          className={classes.changStatusIcon}
                        />
                        <Select
                          style={{color: color}}
                          className={classes.changStatusBox}
                          fullWidth
                          name={`status${index}`}
                          multiple
                          disabled={
                            rulesAdminChangeParcelStatus[
                              authContext.user.role
                            ].indexOf(row.status) === -1
                          }
                          id={row.id}
                          value={[{}]}
                          IconComponent={() => null}
                          renderValue={() => (
                            <span className={classes.renderValue}>
                              {translateParcelsStatus(row.status)}
                            </span>
                          )}
                        >
                          <RadioGroup
                            className={classes.radioGroup}
                            aria-label={`status${index}`}
                            name={`status${index}`}
                            value={'item'}
                            onChange={(
                              e: React.ChangeEvent<{value: unknown}>
                            ) =>
                              props.changeStatus({
                                id: row.id,
                                body: `${e.target.value}`,
                              })
                            }
                          >
                            {rulesParcelStatus[row.status]?.map(
                              (item: any, index: number) => (
                                <FormControlLabel
                                  className={classes.radioGroupItem}
                                  key={index}
                                  checked={row.status === item}
                                  value={item}
                                  control={<Radio />}
                                  label={translateParcelsStatus(item)}
                                />
                              )
                            )}
                          </RadioGroup>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="right">
                      <Link to={`/parcels/details/${row.id}`}>
                        <IconButton
                          aria-label="details"
                          size="small"
                          title="جزئیات محموله"
                          data-cy="details"
                        >
                          <Icon
                            path={mdiFileDocumentOutline}
                            size={1}
                            color={theme.palette.green.main}
                          />
                        </IconButton>
                      </Link>
                      {/* {(row.status === 'SUBMITTED' ||
                        row.status === 'ASSIGNED') && (
                        <Link
                          to={`/parcels-operators/${row.id}/warehouse/${row.destinationId}`}
                        >
                          <IconButton
                            aria-label="details"
                            size="small"
                            title="تخصیص محموله"
                            data-cy="details"
                          >
                            <Icon
                              path={mdiAccountPlus}
                              size={1}
                              color={theme.palette.green.main}
                            />
                          </IconButton>
                        </Link>
                      )} */}
                      {/* <Link to={`/parcels/${row.id}/allLogs`}>
                        <IconButton size="small" title="تاریخچه محموله">
                          <Icon path={mdiHistory} size={1} />
                        </IconButton>
                      </Link> */}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
